'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["appUsageTracker", "data-test-id", "defaultTargetDashboardId", "disabled", "isDrilldown", "label", "onButtonClick", "onSaveError", "onSaveStarted", "onSaveSuccess", "report", "saveUse", "showOnDisable", "shouldBecomeCustom", "tooltipMessage", "tooltipPlacement", "trackingProperties"];
import PropTypes from 'prop-types';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { EVENTS } from '../../utils/usageTracker';
import { getDefaultReportActionEventProperties } from '../report-action-wrapper';
import ActionButton from '../ActionButton';
import { useTracker } from '../../utils/hooks/useTracker';
import { useUserRoleFromScopes } from '../../utils/trackingUtils';
import { useShowDialog } from '../../dialog/ducks';
import { SAVE_REPORT_DIALOG } from '../../dialog/dialogTypes';
import { getTemplateKey } from '../../data/schemas/reportSchema';
import useCreateNewReportProblem from '../shared/problemHooks/useCreateNewReportProblem';
import { CREATE_NEW_REPORT, getSaveReportFlowName, SaveReportUseTypeList } from './saveNewReportTypes';
import { LOADING } from '../problems';
import { getProblemTooltipUse } from '../problemHelpers';
import { noop } from '../shared/noop';
import { jsx as _jsx } from "react/jsx-runtime";
const SaveReportButton = _ref => {
  let {
      appUsageTracker,
      'data-test-id': dataTestId,
      defaultTargetDashboardId,
      disabled: isDisabledViaParentApp = false,
      isDrilldown = false,
      label = /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-action-components.actions.save.button.text.default"
      }),
      onButtonClick = noop,
      onSaveError = noop,
      onSaveStarted = noop,
      onSaveSuccess = noop,
      report,
      saveUse = CREATE_NEW_REPORT,
      showOnDisable = true,
      shouldBecomeCustom = false,
      tooltipMessage: appDefinedTooltip,
      tooltipPlacement,
      trackingProperties = {}
    } = _ref,
    buttonProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  const usageTracker = useTracker(appUsageTracker);
  const showDialog = useShowDialog();
  const {
    problem,
    disabledMessage
  } = useCreateNewReportProblem({
    report,
    saveUse,
    shouldBecomeCustom
  });
  const isDisabled = isDisabledViaParentApp || !!problem;
  const userReportingRole = useUserRoleFromScopes();
  const defaultReportActionEventProperties = getDefaultReportActionEventProperties(report);
  const handleClickSaveReport = () => {
    if (usageTracker) {
      usageTracker.track(EVENTS.saveNewAssetInteraction, Object.assign({
        actionType: 'create',
        action: 'save-new',
        step: 'start',
        whichFlow: getSaveReportFlowName({
          saveUse,
          template: getTemplateKey(report),
          isDrilldown,
          shouldBecomeCustom,
          trackingProperties
        }),
        role: userReportingRole
      }, trackingProperties, defaultReportActionEventProperties, {
        assetType: trackingProperties.assetType || defaultReportActionEventProperties.assetType
      }));
    }
    onButtonClick();
    showDialog({
      dialogType: SAVE_REPORT_DIALOG,
      dialogProps: {
        defaultTargetDashboardId,
        isDrilldown,
        onSaveError,
        onSaveStarted,
        onSaveSuccess,
        report,
        saveUse,
        shouldBecomeCustom,
        trackingProperties,
        usageTracker,
        appUsageTracker
      }
    });
  };
  if (isDisabled && !showOnDisable) {
    return null;
  }
  return /*#__PURE__*/_jsx(ActionButton, Object.assign({
    label: label,
    "data-test-id": problem !== LOADING && dataTestId,
    disabled: isDisabled,
    tooltip: appDefinedTooltip || disabledMessage,
    tooltipPlacement: tooltipPlacement,
    tooltipUse: getProblemTooltipUse(problem),
    onClick: handleClickSaveReport
  }, buttonProps));
};
SaveReportButton.propTypes = {
  appUsageTracker: PropTypes.object.isRequired,
  'data-test-id': PropTypes.string,
  defaultTargetDashboardId: PropTypes.number,
  disabled: PropTypes.bool,
  isDrilldown: PropTypes.bool,
  label: PropTypes.element,
  onButtonClick: PropTypes.func,
  onSaveError: PropTypes.func,
  onSaveStarted: PropTypes.func,
  onSaveSuccess: PropTypes.func,
  report: PropTypes.object.isRequired,
  saveUse: PropTypes.oneOf(SaveReportUseTypeList),
  shouldBecomeCustom: PropTypes.bool,
  showOnDisable: PropTypes.bool,
  tooltipMessage: PropTypes.node,
  tooltipPlacement: PropTypes.string,
  trackingProperties: PropTypes.object
};
export default SaveReportButton;